<template>
    <div>

        <button-white @click="open">
            Bearbeiten
        </button-white>

        <portal to="modal" v-if="edit">
            <modal @close="edit = false">

                <template v-slot:header>Schreibtisch bearbeiten</template>

                <p v-show="item.type==='disabled'" class="font-bold text-red-500">Der Schreibtisch ist gesperrt.</p>

                <div class="flex justify-end">
                    <confirm-dialog @confirmed="enableDesk" v-show="item.type === 'disabled'">
                        <button-secondary class="mr-2">
                            <template slot="icon"><i class="fa fa-unlock-alt"></i></template>
                            Schreibtisch entsperren
                        </button-secondary>
                        <template v-slot:header>Schreibtisch entsperren</template>
                        <template v-slot:question>Soll der Schreibtisch <span class="font-bold">{{ item.name }}</span>
                            entsperrt werden?
                        </template>
                        <template v-slot:confirm>
                            <button-primary>
                                <template slot="icon"><i class="fa fa-unlock-alt"></i></template>
                                Schreibtisch entsperren
                            </button-primary>
                        </template>
                    </confirm-dialog>
                    <confirm-dialog @confirmed="disableDesk" v-show="item.type === 'default'">
                        <button-secondary class="mr-2">
                            <template slot="icon"><i class="fa fa-lock"></i></template>
                            Schreibtisch sperren
                        </button-secondary>
                        <template v-slot:header>Schreibtisch sperren</template>
                        <template v-slot:question>Soll der Schreibtisch <span class="font-bold">{{ item.name }}</span>
                            wirklich gesperrt werden? Dabei werden alle künftigen Buchungen für den Schreibtisch
                            gelöscht und die jeweiligen Nutzer benachrichtigt.

                            <input-text class="mb-4" v-model="reason">
                                Begründung
                            </input-text>
                        </template>
                        <template v-slot:confirm>
                            <button-primary>
                                <template slot="icon"><i class="fa fa-lock"></i></template>
                                Schreibtisch sperren
                            </button-primary>
                        </template>
                    </confirm-dialog>
                    <confirm-dialog @confirmed="remove" v-show="editable">
                        <button-delete>
                            <template v-slot:icon><i class="fa fa-trash"></i></template>
                            Schreibtisch löschen
                        </button-delete>
                        <template v-slot:header>Schreibtisch löschen</template>
                        <template v-slot:question>Soll der Schreibtisch <span class="font-bold">{{ item.name }}</span>
                            wirklich gelöscht werden?
                        </template>
                        <template v-slot:confirm>
                            <button-delete>
                                <template v-slot:icon><i class="fa fa-trash"></i></template>
                                Schreibtisch löschen
                            </button-delete>
                        </template>
                    </confirm-dialog>
                </div>

                <input-text class="mb-4" v-model="item.name">
                    Schreibtisch Bezeichnung z.B. 456
                </input-text>

                <input-text class="mb-4" v-model="item.details">
                    Details
                    <template v-slot:helper>
                        z.B. Fensterplatz, höhenverstellbarer Schreibtisch.
                    </template>
                </input-text>

                <multi-select-small class="mb-4" v-model="selectedAttributes"
                                    :values="attributes.map(a => a.id)" :text="attributes.map(a => a.text)">
                    Ausstattung ({{ selectedAttributes.length }} ausgewählt)
                    <template v-slot:helper>Ausstattungsmerkmale lassen sich vom Nutzer in der App filtern.</template>
                </multi-select-small>

                <select-small class="mb-4" v-model="timeSelectionMode" :values="['slot', 'free']"
                              :text="['Zeitscheiben', 'Stundenweise']">
                    Buchungsmodus
                    <template v-slot:helper>Bei stundenweiser Buchung muss der Nutzer eine konkrete Zeitspanne eingeben,
                        wann der den Schreibtisch braucht. Bei Zeitscheiben werden die konfigurierten Zeitscheiben
                        angezeigt.
                    </template>
                </select-small>

                <div v-if="item.user" class="mb-4">
                    <label class="block text-sm font-medium leading-5 text-gray-700">
                        Besitzer
                        <div class="mt-1 relative rounded-md shadow-sm">
                            <input disabled :value="item.user.name"
                                   class="bg-gray-200 form-input block w-full sm:text-sm sm:leading-5" placeholder=""/>
                        </div>
                    </label>
                </div>


              <input-field class="mb-4">
                Etage
                <template v-slot:input>
                  <select
                      class="w-full form-select h-full py-0 pl-3 pr-7 border-transparent bg-transparent text-gray-500 sm:text-sm sm:leading-5"
                      v-model="floorId">
                    <option v-for="floor in floors" :value="floor.id">{{ floor.name }}</option>
                  </select>
                </template>
              </input-field>
              <div v-if="selectedFloor" class="mb-4">
                <navigation-edit v-model="item" :floor="selectedFloor"></navigation-edit>
              </div>

                <multi-select-small class="mb-4" v-model="disabledWeekdays"
                                    :values="[1,2,3,4,5,6,0]" :text="['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']">
                    Schreibtischsperre
                    <template v-slot:helper>Schreibtisch ist an den ausgewählten Wochentagen nicht buchbar.</template>
                </multi-select-small>

                <toggle-group v-model="showDesks" class="mt-4">
                    Schreibtisch anzeigen
                    <p slot="helper"> Bei Deaktivierung wird der Schreibtisch in der App ausgeblendet</p>
                </toggle-group>


                <div class="flex justify-end">
                    <button-primary v-show="editable" @click="save()">
                        <template slot="icon"><i class="fa fa-floppy-o"></i></template>
                        Speichern
                    </button-primary>
                </div>

            </modal>
        </portal>
    </div>

</template>

<script>
import ButtonSecondary from "./UI/ButtonSecondary";
import Modal from "./Modal";
import ConfirmDialog from "./UI/ConfirmDialog";
import ButtonWhite from "./UI/ButtonWhite";
import InputText from "./UI/InputText";
import InputField from "./UI/InputField";
import NavigationEdit from "./NavigationEdit";
import ButtonPrimary from "./UI/ButtonPrimary";
import MultiSelect from "./UI/MultiSelect.vue";
import Alert from "./UI/Alert.vue";
import ButtonDelete from "./UI/ButtonDelete.vue";
import MultiSelectSmall from "./UI/MultiSelectSmall.vue";
import SelectMenu from "./UI/SelectMenu.vue";
import SelectSmall from "./UI/SelectSmall.vue";
import ToggleGroup from './UI/ToggleGroup.vue';

export default {
    name: "DeskEdit",
    components: {
        SelectSmall,
        SelectMenu,
        MultiSelectSmall,
        ButtonDelete,
        Alert,
        MultiSelect,
        ButtonPrimary,
        NavigationEdit,
        InputField,
        InputText,
        ButtonWhite,
        ConfirmDialog,
        Modal,
        ButtonSecondary,
        ToggleGroup
    },
    props: ['item'],

    inject: ['admin'],

    data: () => ({
        edit: false,
        types: [],
        floors: [],
        floorId: null,
        loading: false,
        attributes: [],
        selectedAttributes: [],
        timeSelectionMode: 'slot',
        reason: '',
        showDesks: true,
        selectedFloor: null,
        disabledWeekdays: [],
    }),

    computed: {
        editable() {
            if (this.admin.is_admin) {
                return true;
            }

            return this.admin.access.write.desks.includes(this.item.id);
        },
    },

    watch: {
      floorId(val) {
        this.selectedFloor = this.floors.find(r => r.id === parseInt(val));
      }
    },

    methods: {
        remove() {
            this.loading = true;
            this.$http.post(`/admin/desks/${this.item.id}`, {
                _method: 'delete',
            })
                .then(() => {
                    this.edit = false;
                    this.loading = false;
                    this.$emit('updated');
                    window.location.reload();
                })
                .catch(error => {
                    this.loading = false;
                    this.$root.$emit('error', error);
                });
        },
        async open() {
            this.showDesks = this.item.desk_type_id === 1;
            this.edit = true;
            this.loading = true;
            const response = await this.$http.get('/admin/floors');
            this.floors = await response.data.data;
            // set floorId after the floors are fetched so that selected floor can be filtered in watcher.
            this.floorId = this.item.floor.id;
            const responseAttributes = await this.$http.get('/admin/attributes')
            this.attributes = await responseAttributes.data.data.map(a => ({
                id: a.id,
                text: a.name,
            }));

            this.selectedAttributes = this.item.attributes.map(a => a.id);
            this.timeSelectionMode = this.item.time_selection_mode;
            this.disabledWeekdays = this.item.disabled_weekdays;

            this.loading = false;
        },
        save() {
            this.$http.post(`/admin/desks/${this.item.id}`, {
                _method: 'put',
                name: this.item.name,
                details: this.item.details,
                floor_id: this.floorId,
                position: this.item.position ?? null,
                attributes: this.selectedAttributes,
                time_selection_mode: this.timeSelectionMode,
                desk_type_id: this.showDesks ? 1 : 2,
                disabled_weekdays: this.disabledWeekdays,
            })
                .then(() => {
                    this.edit = false;
                    this.$root.$emit('alert', 'Gespeichert.');
                    this.$emit('updated');
                })
                .catch(error => {
                    this.$root.$emit('error', error);
                    this.edit = false;
                    this.$emit('updated');
                });
        },
        disableDesk() {
            this.$http.post(`/admin/desks/${this.item.id}`, {
                _method: 'put',
                enabled: false,
                reason: this.reason
            })
                .then(() => {
                    this.edit = false;
                    this.$root.$emit('alert', 'Schreibtisch gesperrt.');
                    this.$emit('updated');
                })
                .catch(error => {
                    this.$root.$emit('error', error);
                    this.edit = false;
                    this.$emit('updated');
                });
        },
        enableDesk() {
            this.$http.post(`/admin/desks/${this.item.id}`, {
                _method: 'put',
                enabled: true,
            })
                .then(() => {
                    this.edit = false;
                    this.$root.$emit('alert', 'Schreibtisch entsperrt.');
                    this.$emit('updated');
                })
                .catch(error => {
                    this.$root.$emit('error', error);
                    this.edit = false;
                    this.$emit('updated');
                });
        },
    },
}
</script>

<style scoped>

</style>
